import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useAuthStore } from '../store/authStore';
import { Document } from '../types';
import { logDocumentAccess } from '../lib/auditLogger';
import toast from 'react-hot-toast';

interface AddDocumentOptions {
  onSuccess?: (data: any) => void;
  onError?: (error: any) => void;
}

export const useDocuments = () => {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();

  // Fetch documents from database with proper user isolation
  const {
    data: documents = [],
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['documents', user?.id],
    queryFn: async () => {
      console.log('Fetching documents for user:', user?.id);

      if (!user?.id) {
        console.warn('No user ID available, cannot fetch documents');
        return [];
      }

      // Query with user filtering to ensure proper isolation
      const { data, error } = await supabase
        .from('documents')
        .select('*')
        .eq('user_id', user.id) // Critical for user isolation
        .order('uploaded_at', { ascending: false });

      if (error) {
        console.error('Error fetching documents:', error);
        throw new Error(error.message);
      }

      console.log(`Found ${data?.length || 0} documents for user ${user.id}`);

      // Transform database records to Document type
      const transformedDocuments = data.map(doc => ({
        id: doc.id,
        name: doc.name,
        type: doc.type,
        size: doc.size,
        uploadedAt: new Date(doc.uploaded_at),
        lastModified: new Date(doc.last_modified),
        isEncrypted: doc.is_encrypted,
        isNotarized: doc.is_notarized,
        notarizationHash: doc.notarization_hash,
        tags: doc.tags || [],
        accessLevel: doc.access_level as 'private' | 'shared' | 'public',
        sharedWith: doc.shared_with || [],
        ipfsHash: doc.ipfs_cid,
      })) as Document[];
      
      // Log document list access
      if (transformedDocuments.length > 0) {
        logDocumentAccess('document_list', 'view', {
          document_count: transformedDocuments.length,
          action_type: 'list_documents'
        });
      }
      
      return transformedDocuments;
    },
    enabled: !!user?.id, // Only run query if user is authenticated
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Add document mutation with proper user ID
  const addDocumentMutation = useMutation({
    mutationFn: async (document: Omit<Document, 'id'> & { id?: string }) => {
      console.log('Adding document for user:', user?.id);

      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      const documentId = document.id || `doc_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      console.log('Adding document with user_id:', user.id);

      // Insert document with explicit user_id
      const { data, error } = await supabase
        .from('documents')
        .insert({
          id: documentId,
          user_id: user.id, // Ensure document belongs to current user
          name: document.name,
          type: document.type,
          size: document.size,
          ipfs_cid: document.ipfsHash || '',
          is_encrypted: document.isEncrypted,
          is_notarized: document.isNotarized,
          notarization_hash: document.notarizationHash,
          access_level: document.accessLevel,
          tags: document.tags,
          shared_with: document.sharedWith,
          uploaded_at: document.uploadedAt.toISOString(),
          last_modified: document.lastModified.toISOString(),
        })
        .select()
        .single();

      if (error) {
        console.error('Error adding document:', error);
        
        // Log failed document creation
        await logDocumentAccess(documentId, 'create', {
          status: 'failed',
          error: error.message,
          document_name: document.name
        });
        
        throw new Error(error.message);
      }

      console.log('Document added successfully:', data);
      
      // Log successful document creation
      await logDocumentAccess(documentId, 'create', {
        status: 'success',
        document_name: document.name,
        document_type: document.type,
        document_size: document.size,
        is_encrypted: document.isEncrypted
      });
      
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['documents', user?.id] });
      toast.success('Document uploaded successfully');
    },
    onError: (error: any) => {
      console.error('Failed to add document:', error);
      toast.error(error.message || 'Failed to upload document');
    },
  });

  // Delete document mutation with optimistic updates and user verification
  const deleteDocumentMutation = useMutation({
    mutationFn: async (documentId: string) => {
      console.log('Deleting document:', documentId);
      
      if (!user?.id) {
        throw new Error('User not authenticated');
      }
      
      // Log document deletion attempt
      await logDocumentAccess(documentId, 'delete', {
        status: 'pending',
        action_type: 'delete_document'
      });
      
      // First verify the document belongs to the current user
      const { data: docCheck, error: checkError } = await supabase
        .from('documents')
        .select('id')
        .eq('id', documentId)
        .eq('user_id', user.id) // Ensure document belongs to current user
        .single();
        
      if (checkError || !docCheck) {
        throw new Error('Document not found or you do not have permission to delete it');
      }
      
      const { error } = await supabase
        .from('documents')
        .delete()
        .eq('id', documentId)
        .eq('user_id', user.id); // Double-check user_id for security

      if (error) {
        console.error('Error deleting document:', error);
        
        // Log failed deletion
        await logDocumentAccess(documentId, 'delete', {
          status: 'failed',
          error: error.message
        });
        
        throw new Error(error.message);
      }
      
      console.log('Document deleted successfully:', documentId);
      
      // Log successful deletion
      await logDocumentAccess(documentId, 'delete', {
        status: 'success',
        action_type: 'delete_document'
      });
      
      return documentId;
    },
    onMutate: async (documentId: string) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['documents', user?.id] });

      // Snapshot the previous value
      const previousDocuments = queryClient.getQueryData(['documents', user?.id]);

      // Optimistically remove the document from the UI
      queryClient.setQueryData(['documents', user?.id], (old: Document[] = []) =>
        old.filter(doc => doc.id !== documentId)
      );

      // Return a context object with the snapshotted value
      return { previousDocuments, documentId };
    },
    onError: (err, documentId, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousDocuments) {
        queryClient.setQueryData(['documents', user?.id], context.previousDocuments);
      }
      console.error('Failed to delete document:', err);
      toast.error(err.message || 'Failed to delete document');
    },
    onSuccess: (documentId) => {
      toast.success('Document deleted successfully');
      console.log('Document deletion completed:', documentId);
    },
    onSettled: () => {
      // Always refetch after error or success to ensure we have the latest data
      queryClient.invalidateQueries({ queryKey: ['documents', user?.id] });
    },
  });

  // Update document mutation with optimistic updates and user verification
  const updateDocumentMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<Document> }) => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }
      
      // Log document update attempt
      await logDocumentAccess(id, 'update', {
        status: 'pending',
        updates: Object.keys(updates),
        action_type: 'update_document'
      });
      
      // First verify the document belongs to the current user
      const { data: docCheck, error: checkError } = await supabase
        .from('documents')
        .select('id')
        .eq('id', id)
        .eq('user_id', user.id) // Ensure document belongs to current user
        .single();
        
      if (checkError || !docCheck) {
        throw new Error('Document not found or you do not have permission to update it');
      }
      
      const { data, error } = await supabase
        .from('documents')
        .update({
          ...(updates.name && { name: updates.name }),
          ...(updates.tags && { tags: updates.tags }),
          ...(updates.accessLevel && { access_level: updates.accessLevel }),
          ...(updates.sharedWith && { shared_with: updates.sharedWith }),
          ...(updates.isNotarized !== undefined && { is_notarized: updates.isNotarized }),
          ...(updates.notarizationHash && { notarization_hash: updates.notarizationHash }),
          last_modified: new Date().toISOString(),
        })
        .eq('id', id)
        .eq('user_id', user.id) // Double-check user_id for security
        .select()
        .single();

      if (error) {
        console.error('Error updating document:', error);
        
        // Log failed update
        await logDocumentAccess(id, 'update', {
          status: 'failed',
          error: error.message,
          updates: Object.keys(updates)
        });
        
        throw new Error(error.message);
      }

      // Log successful update
      await logDocumentAccess(id, 'update', {
        status: 'success',
        updates: Object.keys(updates),
        action_type: 'update_document'
      });

      return data;
    },
    onMutate: async ({ id, updates }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['documents', user?.id] });

      // Snapshot the previous value
      const previousDocuments = queryClient.getQueryData(['documents', user?.id]);

      // Optimistically update to the new value
      queryClient.setQueryData(['documents', user?.id], (old: Document[] = []) =>
        old.map(doc => 
          doc.id === id 
            ? { 
                ...doc, 
                ...updates,
                lastModified: new Date()
              } 
            : doc
        )
      );

      // Return a context object with the snapshotted value
      return { previousDocuments };
    },
    onError: (err, variables, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousDocuments) {
        queryClient.setQueryData(['documents', user?.id], context.previousDocuments);
      }
      console.error('Failed to update document:', err);
      toast.error(err.message || 'Failed to update document');
    },
    onSettled: () => {
      // Always refetch after error or success to ensure we have the latest data
      queryClient.invalidateQueries({ queryKey: ['documents', user?.id] });
    },
    onSuccess: (data, variables) => {
      // Show success message for notarization specifically
      if (variables.updates.isNotarized) {
        toast.success('Document notarization status updated successfully');
      } else {
        toast.success('Document updated successfully');
      }
    },
  });

  // Custom addDocument function that accepts options
  const addDocument = (document: Omit<Document, 'id'> & { id?: string }, options?: AddDocumentOptions) => {
    addDocumentMutation.mutate(document, {
      onSuccess: (data) => {
        options?.onSuccess?.(data);
      },
      onError: (error) => {
        options?.onError?.(error);
      }
    });
  };

  // Enhanced deleteDocument function with confirmation
  const deleteDocument = async (documentId: string, documentName?: string) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete "${documentName || 'this document'}"? This action cannot be undone.`
    );
    
    if (confirmed) {
      deleteDocumentMutation.mutate(documentId);
    } else {
      // Log cancelled deletion
      await logDocumentAccess(documentId, 'delete', {
        status: 'cancelled',
        action_type: 'delete_cancelled'
      });
    }
  };

  // Enhanced updateDocument function with real-time UI updates
  const updateDocument = async (id: string, updates: Partial<Document>) => {
    return updateDocumentMutation.mutateAsync({ id, updates });
  };

  // Function to refresh a specific document with user verification
  const refreshDocument = async (documentId: string) => {
    try {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }
      
      const { data, error } = await supabase
        .from('documents')
        .select('*')
        .eq('id', documentId)
        .eq('user_id', user.id) // Ensure document belongs to current user
        .single();

      if (error) throw error;

      // Log document refresh
      await logDocumentAccess(documentId, 'view', {
        action_type: 'refresh_document',
        status: 'success'
      });

      // Update the specific document in the cache
      queryClient.setQueryData(['documents', user?.id], (old: Document[] = []) =>
        old.map(doc => 
          doc.id === documentId 
            ? {
                id: data.id,
                name: data.name,
                type: data.type,
                size: data.size,
                uploadedAt: new Date(data.uploaded_at),
                lastModified: new Date(data.last_modified),
                isEncrypted: data.is_encrypted,
                isNotarized: data.is_notarized,
                notarizationHash: data.notarization_hash,
                tags: data.tags || [],
                accessLevel: data.access_level as 'private' | 'shared' | 'public',
                sharedWith: data.shared_with || [],
                ipfsHash: data.ipfs_cid,
              }
            : doc
        )
      );

      return data;
    } catch (error: any) {
      console.error('Failed to refresh document:', error);
      
      // Log failed refresh
      await logDocumentAccess(documentId, 'view', {
        action_type: 'refresh_document',
        status: 'failed',
        error: error.message
      });
      
      throw error;
    }
  };

  return {
    documents,
    isLoading,
    error,
    refetch,
    addDocument,
    deleteDocument,
    updateDocument,
    refreshDocument,
    isAddingDocument: addDocumentMutation.isPending,
    isDeletingDocument: deleteDocumentMutation.isPending,
    isUpdatingDocument: updateDocumentMutation.isPending,
  };
};